import {Inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {firstValueFrom} from 'rxjs';
import {environment} from '../../environments/environment';
import {TenantBootstrap, TenantModuleCode} from '../models/tenant/tenant-bootstrap.model';
import {TENANT_RESOLUTION} from '../config/tenant-resolution.config';

const STORAGE_KEY = 'bc.tenant.bootstrap';

/**
 * Resolves the current organization from the subdomain and bootstraps the tenant BEFORE login.
 *
 * Flow: `codewise.bizcombotech.com` → orgCode `codewise` → Billing bootstrap → we learn the
 * tenantId (sent as `X-BC-Tenant` on every module API call), which modules the org is licensed
 * for, and the database names backing it. The bootstrap call is anonymous, so it uses the
 * interceptor-free HttpClient.
 */
@Injectable({providedIn: 'root'})
export class TenantContextService {
  private bootstrapData?: TenantBootstrap;

  constructor(@Inject('NoInterceptorHttpClient') private http: HttpClient) {
    // Restore from sessionStorage so a hard refresh doesn't need to re-bootstrap before the
    // interceptor needs the tenant id.
    const cached = sessionStorage.getItem(STORAGE_KEY);
    if (cached) {
      try {
        this.bootstrapData = JSON.parse(cached);
      } catch {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    }
  }

  /**
   * Derive the organization code from the host (first subdomain label). Resolution rules
   * (prefixes to strip, ignored hosts, explicit overrides) live in `tenant-resolution.config.ts`.
   */
  resolveOrgCode(): string | null {
    const host = window.location.hostname; // e.g. codewise.bizcombotech.com
    const labels = host.split('.');

    // localhost / 127.0.0.1 / apex domain → no subdomain to read; fall back.
    const isLocal = host === 'localhost' || host === '127.0.0.1' || labels.length < 3;
    if (isLocal) return this.fallbackOrgCode();

    let first = labels[0]?.toLowerCase();
    if (!first) return this.fallbackOrgCode();

    // 1. Explicit override for this exact label wins.
    if (TENANT_RESOLUTION.orgAliases[first]) return TENANT_RESOLUTION.orgAliases[first];

    // 2. Strip a configured environment prefix so the label matches the Billing org code
    //    (e.g. `dev-codewise` → `codewise`).
    const prefix = TENANT_RESOLUTION.stripPrefixes.find(p => first!.startsWith(`${p}-`));
    if (prefix) first = first.slice(prefix.length + 1);

    // 3. Non-tenant hosts (www/app) fall back like local.
    if (!first || TENANT_RESOLUTION.ignoredHosts.includes(first)) return this.fallbackOrgCode();

    return first;
  }

  /** No subdomain org available: allow a `?org=` override for local dev, then env default. */
  private fallbackOrgCode(): string | null {
    const fromQuery = new URLSearchParams(window.location.search).get('org');
    return fromQuery || environment.apiConfig.billingAPI.devOrgCode || null;
  }

  /** Called from APP_INITIALIZER. Never throws — a failed bootstrap leaves the app un-tenanted. */
  async bootstrap(): Promise<void> {
    const orgCode = this.resolveOrgCode();
    if (!orgCode) return;

    // endpoint already includes the .../api/licensing/v1 base, so only append the action.
    const base = environment.apiConfig.billingAPI.endpoint.replace(/\/$/, '');
    const env = environment.apiConfig.billingAPI.environment;
    const url = `${base}/bootstrap?orgCode=${encodeURIComponent(orgCode)}&environment=${env}`;

    try {
      const data = await firstValueFrom(this.http.get<TenantBootstrap>(url));
      this.bootstrapData = data;
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      // Unknown subdomain or Billing unavailable — leave un-tenanted; guards/UI handle it.
      this.bootstrapData = undefined;
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }

  get isResolved(): boolean {
    return !!this.bootstrapData;
  }

  get tenantId(): string | null {
    return this.bootstrapData?.tenantId ?? null;
  }

  get organizationId(): string | null {
    return this.bootstrapData?.organizationId ?? null;
  }

  get organizationName(): string | null {
    return this.bootstrapData?.organizationName ?? null;
  }

  get isActive(): boolean {
    return this.bootstrapData?.tenantStatus === 'Active';
  }

  get bootstrap$(): TenantBootstrap | undefined {
    return this.bootstrapData;
  }

  /** Whether the organization is licensed for a module (by Billing module code). */
  hasModule(code: TenantModuleCode | string): boolean {
    const modules = this.bootstrapData?.entitlements?.modules ?? [];
    return modules.some(m => m.moduleCode === code);
  }

  clear(): void {
    this.bootstrapData = undefined;
    sessionStorage.removeItem(STORAGE_KEY);
  }
}
